/* Add Sticker type as attribute here. */
import type { BackgroundOption } from '@athena-lib/BackgroundSelector';
import type { ProductItemAttributesValue } from '@athena-lib/SizeBox';

export type Sticker = {
  path: string;
  width: number;
  height: number;
  pathWidth?: number;
  pathHeight?: number;
  previewImage: string;
  originOffset?: { x: number; y: number };
  whiteLayerMasks: WhiteLayerMask[];
  original?: {
    height: number;
    width: number;
    originOffset: { x: number; y: number };
    path: string;
    pathHeight: number;
    pathWidth: number;
  };
};

export type MaterialColorEffectSelectorProps = {
  mask: WhiteLayerMask;
  onUpdateMask: (mask: WhiteLayerMask) => void;
  disabled?: boolean;
  isLoading: boolean;
};

export type StickerPreviewDebugSettings = {
  showSelectedMaterial: boolean;
};

export type StickerPreviewProps = {
  baseProps: PreviewBaseProps;
  className?: string;
  settings: PreviewSettings;
  staticUrl: string;
  isLoading: boolean;
  visible: boolean;
  wiggle: boolean;
  productItemDimensions?: ProductItemDimensions;
  maskSource?: MaskSource;
};

export type PreviewBaseProps = {
  sticker: Sticker;
  material: Material;
  laminate: ProductItemAttributesValue;
  patternScale: number;
  maskBackground: WhiteLayerMaskMode;
  useImageAlphaAsWhitelayer?: boolean;
};

export type PreviewSettings = {
  laminateLayerEnabled: boolean;
  patternScale: number;
  lightweight: boolean;
  animationMode: StickerPreviewAnimationMode;
  showFps: boolean;
  holoIndex: number;
  effectLayerEnabled: boolean;
  backgroundOption: BackgroundOption;
  fullSize?: boolean;
  adminPreview?: boolean;
  showMeasures?: boolean;
};

export enum StickerPreviewAnimationMode {
  Loop = 'Loop',
  OnMouseMove = 'OnMouseMove',
  OnMouseMoveReset = 'OnMouseMoveReset',
  OnDeviceOrientation = 'OnDeviceOrientation',
  None = 'None',
}

export type PreviewSettingsProps = {
  materialOptions: Material[];
  laminateOptions: Laminate[];
  selectedMaterial: Material;
  laminateLayerEnabled: boolean;
  selectedLaminate: Laminate;
  patternScale: number;
  lightweight: boolean;
  animationMode: StickerPreviewAnimationMode;
  showFps: boolean;
  holoIndex: number;
  effectLayerEnabled: boolean;
};

export type Material = {
  type: MaterialName;
  title: string;
  isMetal: boolean;
  metallicOpacity?: number | ((laminateName: string) => number);
  enabled: boolean;
  laminateSettings: {
    enabled: boolean;
    laminateOpacity?: number;
  };
};

export enum CutlineType {
  Path = 'path',
  Rect = 'rect',
  Circle = 'circle',
  Rounded = 'rounded',
}

export enum MaterialName {
  Vinyl = 'white',
  Holographic = 'holographic',
  Prismatic = 'prismatic',
  Glitter = 'glitter',
  Metal = 'metal',
  Recycled = 'kraft_paper',
  Pixie = 'pixie_dust',
  Brushed = 'brushed_alloy',
  Mirror = 'mirror',
  Clear = 'clear',
  Gitd = 'gitd_industrial',
  WhiteRemovable = 'white_removable',
  WhiteHiTack = 'white_hi_tack',
  Reflective = 'reflective',
  WhiteThin = 'white_thin',
  WhiteWall = 'white_wall',
  WhiteSturdy = 'white_sturdy',
  ClearThin = 'clear_thin',
  KraftThin = 'kraft_thin',
  SilverThin = 'silver_thin',
  SatinMatte = 'satin_matte',
}

export type Laminate =
  | LaminateType.Glossy
  | LaminateType.Matte
  | LaminateType.Cracked
  | LaminateType.SatinMatte
  | LaminateType.Uncoated
  | LaminateType.GlossyThin
  | LaminateType.GlossyThinNoUV;

export enum LaminateType {
  Glossy = 'glossy_uv',
  Matte = 'soft_touch',
  SatinMatte = 'satin_matte',
  Cracked = 'cracked_ice',
  Uncoated = 'uncoated',
  GlossyThin = 'glossy_thin',
  GlossyThinNoUV = 'glossy_thin_no_uv',
}

export enum WhiteLayerMaskMode {
  Color = 'Color',
  Mix = 'Mix',
  Effect = 'Effect',
}

export enum MaskFillColor {
  Color = 'white',
  Mix = 'gray',
  Effect = 'black',
}

export type WhiteLayerMask = {
  colorRange: string;
  maskImgUrl: string;
  mode: WhiteLayerMaskMode;
};

export type SpriteSheetData = {
  indices: SheetPos[];
  width: number;
  height: number;
  frameWidth: number;
  frameHeight: number;
  imgSrc: string;
  patternScale: number;
};

export type SheetPos = {
  x: number;
  y: number;
};

export type StickerPreviewLayerProps = {
  selectedMaterial: Material;
  settings: PreviewSettings;
  uniqueId?: string;
  basePlateLayerStyle?: string;
  printLayerStyle?: string;
};

/* Copied from atlas. */
export type LoadedFont = {
  fontFamily: string;
  url: string;
};

export enum MaskSource {
  COMBINED = 'combinedMasks',
  SAVED_MASK = 'savedMaskOptions',
  CHANGED_SAVED_MASK = 'changedSavedMaskOptions',
  GENERATED = 'generatedMasks',
  UPLOADED = 'uploadedMask',
}

export type ProductItemDimensions = {
  widthMM: number;
  heightMM: number;
  imperialUnits: boolean;
};
